// The 32 NFL teams with curated palettes. Team colors are not trademarks;
// logos are, and none are included here. `onPrimary` is computed once so the theme
// never has to.

import type { ProTeam } from '@fsp/sports-core';
import { onColor } from '@fsp/theme';

// THE ID IS THE DATA'S CODE FOR THE CLUB, the abbreviation what a person
// reads. They differ for one club: every nflverse file since the move spells
// the Rams `LA`, so `players.pro_team`, the games and the club's own row all
// say `LA`, and a person reads `LAR`, which is not mistaken for the Chargers.
// Keyed `LAR`, the Rams found no colors, name or division for any Rams player
// (2026-10-25).
type Row = [
  id: string,
  location: string,
  shortName: string,
  conf: string,
  div: string,
  primary: string,
  secondary: string,
  abbreviation?: string,
];

const ROWS: readonly Row[] = [
  ['ARI', 'Arizona', 'Cardinals', 'NFC', 'West', '#97233F', '#FFB612'],
  ['ATL', 'Atlanta', 'Falcons', 'NFC', 'South', '#A71930', '#000000'],
  ['BAL', 'Baltimore', 'Ravens', 'AFC', 'North', '#241773', '#9E7C0C'],
  ['BUF', 'Buffalo', 'Bills', 'AFC', 'East', '#00338D', '#C60C30'],
  ['CAR', 'Carolina', 'Panthers', 'NFC', 'South', '#0085CA', '#101820'],
  ['CHI', 'Chicago', 'Bears', 'NFC', 'North', '#0B162A', '#C83803'],
  ['CIN', 'Cincinnati', 'Bengals', 'AFC', 'North', '#FB4F14', '#000000'],
  ['CLE', 'Cleveland', 'Browns', 'AFC', 'North', '#311D00', '#FF3C00'],
  ['DAL', 'Dallas', 'Cowboys', 'NFC', 'East', '#041E42', '#869397'],
  ['DEN', 'Denver', 'Broncos', 'AFC', 'West', '#FB4F14', '#002244'],
  ['DET', 'Detroit', 'Lions', 'NFC', 'North', '#0076B6', '#B0B7BC'],
  ['GB', 'Green Bay', 'Packers', 'NFC', 'North', '#203731', '#FFB612'],
  ['HOU', 'Houston', 'Texans', 'AFC', 'South', '#03202F', '#A71930'],
  ['IND', 'Indianapolis', 'Colts', 'AFC', 'South', '#002C5F', '#A2AAAD'],
  ['JAX', 'Jacksonville', 'Jaguars', 'AFC', 'South', '#006778', '#D7A22A'],
  ['KC', 'Kansas City', 'Chiefs', 'AFC', 'West', '#E31837', '#FFB81C'],
  ['LV', 'Las Vegas', 'Raiders', 'AFC', 'West', '#000000', '#A5ACAF'],
  ['LAC', 'Los Angeles', 'Chargers', 'AFC', 'West', '#0080C6', '#FFC20E'],
  ['LA', 'Los Angeles', 'Rams', 'NFC', 'West', '#003594', '#FFA300', 'LAR'],
  ['MIA', 'Miami', 'Dolphins', 'AFC', 'East', '#008E97', '#FC4C02'],
  ['MIN', 'Minnesota', 'Vikings', 'NFC', 'North', '#4F2683', '#FFC62F'],
  ['NE', 'New England', 'Patriots', 'AFC', 'East', '#002244', '#C60C30'],
  ['NO', 'New Orleans', 'Saints', 'NFC', 'South', '#D3BC8D', '#101820'],
  ['NYG', 'New York', 'Giants', 'NFC', 'East', '#0B2265', '#A71930'],
  ['NYJ', 'New York', 'Jets', 'AFC', 'East', '#125740', '#000000'],
  ['PHI', 'Philadelphia', 'Eagles', 'NFC', 'East', '#004C54', '#A5ACAF'],
  ['PIT', 'Pittsburgh', 'Steelers', 'AFC', 'North', '#FFB612', '#101820'],
  ['SF', 'San Francisco', '49ers', 'NFC', 'West', '#AA0000', '#B3995D'],
  ['SEA', 'Seattle', 'Seahawks', 'NFC', 'West', '#002244', '#69BE28'],
  ['TB', 'Tampa Bay', 'Buccaneers', 'NFC', 'South', '#D50A0A', '#FF7900'],
  ['TEN', 'Tennessee', 'Titans', 'AFC', 'South', '#0C2340', '#4B92DB'],
  ['WAS', 'Washington', 'Commanders', 'NFC', 'East', '#5A1414', '#FFB612'],
];

export const nflTeams: readonly ProTeam[] = ROWS.map(
  ([id, location, shortName, conference, division, primary, secondary, abbreviation]) => ({
    id,
    name: `${location} ${shortName}`,
    shortName,
    abbreviation: abbreviation ?? id,
    location,
    conference,
    division: `${conference} ${division}`,
    palette: { primary, secondary, onPrimary: onColor(primary) },
  }),
);
